import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "", travel: "", service: "", message: "",
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Missing details", description: "Please fill your name, email and phone.", variant: "destructive" });
      return;
    }
    toast({ title: "Message sent!", description: "Our team will reach out within 24 hours." });
    setForm({ name: "", phone: "", email: "", city: "", travel: "", service: "", message: "" });
  };

  const contacts = [
    { Icon: Phone, label: "CALL US", value: "+91 94963 13677", href: "tel:+919496313677" },
    { Icon: Mail, label: "EMAIL ADDRESS", value: "kitesflytravels@gmail.com", href: "mailto:kitesflytravels@gmail.com" },
    { Icon: Clock, label: "OFFICE HOURS", value: "Available 24 x 7" },
    { Icon: MapPin, label: "LOCATION", value: "Yamuna Arcade, Railway Station Link Road, Palayam, Kozhikode" },
  ];

  return (
    <main className="pt-32">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">CONTACT US</div>
        <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-800 mt-3 max-w-3xl leading-tight">Let's plan something unforgettable.</h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">Whether you're planning a solo adventure or a family holiday, our travel consultants are ready to help.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-14">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contacts.map((c) => (
                <div key={c.label} className="bg-white p-5 rounded-2xl border border-slate-100 flex items-center gap-3 lift">
                  <div className="w-11 h-11 rounded-xl bg-[#f9fcf3] text-[#70a308] flex items-center justify-center"><c.Icon className="w-5 h-5" /></div>
                  <div>
                    <div className="text-[10px] tracking-widest text-slate-500 font-semibold">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="text-sm font-semibold text-slate-800 hover:text-[#5c8606]">{c.value}</a>
                    ) : (
                      <div className="text-sm font-semibold text-slate-800">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-gradient-to-br from-[#84c009] to-[#4f7700] text-white rounded-2xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><MessageCircle className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="text-sm opacity-90">Prefer WhatsApp?</div>
                <div className="font-bold">Chat with our India team</div>
              </div>
              <a href="https://wa.me/919496313677" target="_blank" rel="noreferrer">
                <Button variant="secondary" className="bg-white text-[#5c8606] hover:bg-white/90 rounded-full font-semibold">Chat now</Button>
              </a>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <iframe
                title="Kites Fly Office - Yamuna Arcade, Palayam, Kozhikode"
                src="https://www.google.com/maps?q=Yamuna+Arcade+Railway+Station+Link+Road+Palayam+Kozhikode&output=embed"
                width="100%" height="260" style={{ border: 0 }} loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm h-fit">
            <h3 className="text-2xl font-bold text-slate-800 mb-1">Send us a message</h3>
            <p className="text-sm text-slate-500 mb-6">We usually respond within a few hours during office hours.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Full Name *</label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="John Doe" className="mt-1 h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Phone Number *</label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91 000 000 0000" className="mt-1 h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="john@example.com" className="mt-1 h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">City</label>
                <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="Your city" className="mt-1 h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Travel Destination</label>
                <Input value={form.travel} onChange={(e) => setForm({...form, travel: e.target.value})} placeholder="e.g. Bali" className="mt-1 h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Service</label>
                <Select value={form.service} onValueChange={(v) => setForm({...form, service: v})}>
                  <SelectTrigger className="h-11 mt-1 rounded-lg"><SelectValue placeholder="Select Service" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tour">Tour Packages</SelectItem>
                    <SelectItem value="pilgrim">Pilgrim Packages</SelectItem>
                    <SelectItem value="passport">Passport Services</SelectItem>
                    <SelectItem value="tickets">Flight, Train &amp; Bus Tickets</SelectItem>
                    <SelectItem value="visa">Visa Services</SelectItem>
                    <SelectItem value="hotel">Hotel Reservations</SelectItem>
                    <SelectItem value="insurance">Travel Insurance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Message</label>
                <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us about your travel plans..." className="mt-1 min-h-[120px] rounded-lg" />
              </div>
            </div>
            <Button type="submit" className="w-full mt-5 h-12 bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] text-white rounded-lg font-semibold">
              <Send className="w-4 h-4 mr-2" /> Send Message
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
}
